import { DateRangeKeyword, DateRangeV2, FilterBar2ListItem, FilterBar2Value } from '@bringg/react-components';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { LookupConsts } from '@bringg/dashboard-sdk';
import { Moment } from 'moment';
import moment from 'moment';
import { Filters, RunFilters } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import { RootStore } from '@bringg-frontend/bringg-web-legacy';

import {
	DateRangeFieldKeys,
	DateRangeFilterBarValue,
	FilterBarListItemKeys,
	FiltersObject,
	InitialFilterBarObject,
	supportedFiltersList,
	TaskFiltersObject,
	RunFiltersObject
} from '../types';
import { mapListValues } from '../../../../../runs/runs-filter/runs-filter-utils';
import { NO_TEAM_VALUE } from '../../team-selector/components/no-team-option/no-team-option';
import { dayPickerDynamicOptions } from '../../day-picker/day-picker';

const mapQueryValuesToFilterBar = (
	values: FiltersObject,
	filterBarFilters: FilterBar2ListItem[]
): InitialFilterBarObject => {
	// filter filterObject to fit the filter bar item list
	const valuesForBar = filterBarFilters.reduce((acc, item) => {
		if (values[item.name]) {
			acc[item.name] = values[item.name];
		}

		return acc;
	}, {});

	const dateRangeFields = DATE_RANGE_FIELDS.reduce((acc, dateKey) => {
		if (valuesForBar[dateKey]) {
			const [from, to] = valuesForBar[dateKey] as DateRangeFilterBarValue;

			if (DateRangeV2.isPairOfDateRangeKeywords([from, to])) {
				acc[dateKey] = valuesForBar[dateKey];
			} else {
				acc[dateKey] = [moment(from), moment(to)];
			}
		}

		return acc;
	}, {} as { [key in DateRangeFieldKeys]: DateRangeFilterBarValue });

	const singleDayFields = SINGLE_DAY_FIELDS.reduce((acc, dateKey) => {
		if (values[dateKey]) {
			acc[dateKey] = moment(values[dateKey]);
		}

		return acc;
	}, {});

	return {
		...valuesForBar,
		...dateRangeFields,
		...singleDayFields
	};
};

const DATE_RANGE_FIELDS = [
	FilterBarListItemKeys.TIME_WINDOW,
	FilterBarListItemKeys.SLA,
	FilterBarListItemKeys.RESERVED_UNTIL
];

const SINGLE_DAY_FIELDS = [FilterBarListItemKeys.RUN_TIME_WINDOW];

const mapValuesFromFilterBar = (values: { [key: string]: any }) => {
	const dateRangeFields = DATE_RANGE_FIELDS.reduce((acc, dateKey) => {
		if (values[dateKey]) {
			const [from, to] = values[dateKey] as DateRangeFilterBarValue;

			if (DateRangeV2.isPairOfDateRangeKeywords([from, to])) {
				acc[dateKey] = values[dateKey];
			} else {
				acc[dateKey] = [(from as Moment).toISOString(), (to as Moment).toISOString()];
			}
		}

		return acc;
	}, {});

	const singleDayFields = SINGLE_DAY_FIELDS.reduce((acc, dateKey) => {
		if (values[dateKey]) {
			acc[dateKey] = values[dateKey].format('YYYY-MM-DD');
		}

		return acc;
	}, {});

	return { ...values, ...dateRangeFields, ...singleDayFields };
};

const cleanFilterEntries = (query: FiltersObject): FiltersObject =>
	Object.fromEntries(
		Object.entries(query).filter(([key]) => {
			return supportedFiltersList.includes(key as keyof FiltersObject) && query[key] !== undefined;
		})
	);

const handleSearch = async (value: string, key): Promise<FilterBar2Value[]> => {
	const lookUp = getRootEnv().dashboardSdk.sdk.v2.lookup;

	let listValues;

	const params: LookupConsts.BaseSearchQuery = {
		query: value
	};

	switch (key) {
		case FilterBarListItemKeys.ROUTES:
			listValues = await lookUp.getRuns(params);
			break;

		case FilterBarListItemKeys.DRIVERS:
			listValues = await lookUp.getUsers({ ...params, roles: ['driver'] });
			break;

		case FilterBarListItemKeys.PLANNED_ROUTE:
			listValues = await lookUp.getPlannedRoutes(params);
			break;

		case FilterBarListItemKeys.TASKS:
			listValues = await lookUp.getTasks(params);
			break;

		default:
			return [];
	}

	return listValues?.items?.length ? mapListValues(listValues) : [];
};

const getNormalizedDateRangeFilters = (values: FiltersObject): { [key in DateRangeFieldKeys]?: [string, string] } => {
	return DATE_RANGE_FIELDS.reduce((acc, dateKey) => {
		if (values[dateKey]) {
			const [from, to] = values[dateKey] as [string, string];

			if (DateRangeV2.isPairOfDateRangeKeywords([from, to])) {
				if (from === to) {
					const [startDate, endDate] = DateRangeV2.parseKeywordToDateRange(from as DateRangeKeyword);
					acc[dateKey] = [startDate.toISOString(), endDate.toISOString()];
				}
			}
		}

		return acc;
	}, {});
};

const addTeamChildren = (allIds: number[], teamId: number) => {
	allIds.push(teamId);

	const children = getRoot<RootStore>().data.teamsStore.get(teamId as number)?.childrenTeamIds;

	if (children?.length) {
		allIds.push(...children);
	}

	return allIds;
};

const getNormalizeTeamFilter = (filters: FiltersObject) =>
	filters[FilterBarListItemKeys.TEAMS]
		? {
				[FilterBarListItemKeys.TEAMS]: filters[FilterBarListItemKeys.TEAMS]
					.map(id => (id === NO_TEAM_VALUE ? null : id))
					.reduce(addTeamChildren, [])
		  }
		: {};

const getNormalizeRunTimeWindow = (runTimeWindow: string) => {
	if (dayPickerDynamicOptions.includes(runTimeWindow as DateRangeKeyword)) {
		const [startTime, endTime] = DateRangeV2.parseKeywordToDateRange(runTimeWindow as DateRangeKeyword);

		return {
			startTime: startTime.toISOString(),
			endTime: endTime.toISOString()
		};
	}

	return {
		startTime: moment(runTimeWindow).startOf('day').toISOString(),
		endTime: moment(runTimeWindow).endOf('day').toISOString()
	};
};

const mapFiltersToServer = (filters: TaskFiltersObject): Filters => {
	const { unscheduled, timeWindow, sla, reservedUntil, selectedTasks, ...restOfFilters } = filters;
	const mappedObj: Filters = { ...restOfFilters };

	if (unscheduled !== undefined) {
		mappedObj.unscheduled = unscheduled === 1;
	}

	if (timeWindow) {
		const [from, to] = timeWindow;
		mappedObj.timeWindowStart = from;
		mappedObj.timeWindowEnd = to;
	}

	if (sla) {
		const [from, to] = sla;
		mappedObj.slaStartTime = from;
		mappedObj.slaEndTime = to;
	}

	if (reservedUntil) {
		const [from, to] = reservedUntil;
		mappedObj.reservedUntilStartTime = from;
		mappedObj.reservedUntilEndTime = to;
	}

	if (selectedTasks) {
		mappedObj.ids = selectedTasks;
	}

	return mappedObj;
};

const mapRunsFiltersToServer = (filters: RunFiltersObject): RunFilters => {
	const { selectedTeams, runStatus, selectedUsers, selectedRuns, runTimeWindow, plannedRouteIds } = filters;

	return {
		selectedTeams,
		runStatus,
		plannedRouteIds,
		userIds: selectedUsers?.length ? selectedUsers : undefined,
		ids: selectedRuns?.length ? selectedRuns : undefined,
		...getNormalizeRunTimeWindow(runTimeWindow)
	};
};

const count = ({ filtersObj, keysToIgnore }: { filtersObj?: FiltersObject; keysToIgnore: FilterBarListItemKeys[] }) =>
	filtersObj
		? Object.keys(filtersObj).filter(
				key =>
					!keysToIgnore.includes(key as FilterBarListItemKeys) &&
					filtersObj[key] !== undefined &&
					(Array.isArray(filtersObj[key]) ? filtersObj[key]?.length : true)
		  ).length
		: null;

function isPartOfRunFilters(key: FilterBarListItemKeys): key is keyof RunFiltersObject {
	return [
		FilterBarListItemKeys.TEAMS,
		FilterBarListItemKeys.DRIVERS,
		FilterBarListItemKeys.ROUTES,
		FilterBarListItemKeys.STATUS,
		FilterBarListItemKeys.RUN_TIME_WINDOW,
		FilterBarListItemKeys.PLANNED_ROUTE
	].includes(key);
}

function splitFiltersToRunsAndTasks(filters: FiltersObject) {
	const runFilters: RunFiltersObject = {};
	const taskFilters: TaskFiltersObject = {};

	for (const key of Object.keys(filters)) {
		if (isPartOfRunFilters(key as FilterBarListItemKeys)) {
			runFilters[key] = filters[key];
		} else {
			taskFilters[key] = filters[key];
		}
	}

	return { runFilters, taskFilters };
}

export const monitoringFiltersProvider = {
	mapQueryValuesToFilterBar,
	mapValuesFromFilterBar,
	getNormalizedDateRangeFilters,
	getNormalizeTeamFilter,
	getNormalizeRunTimeWindow,
	mapFiltersToServer,
	mapRunsFiltersToServer,
	cleanFilterEntries,
	handleSearch,
	count,
	splitFiltersToRunsAndTasks
};
